// src/pages/laporan/LaporanEdit.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Save, Send, Upload, X,
  FileText, AlertTriangle, Target, BookOpen, Paperclip, Eye,
  CheckCircle, Info, Clock, RefreshCw
} from 'lucide-react';
import { Button, Card, Input, Select, Textarea, Breadcrumb, Badge, Loading, LogoLoadingMark } from '../../components/ui';
import { KategoriMasalah, StandarSPMI, IndikatorSPMI, TingkatRisiko } from '../../types';
import { laporanService } from '../../services/laporan';
import { adminService } from '../../services/admin';
import toast from 'react-hot-toast';
import { useSystemSettingsStore } from '../../store/systemSettingsStore';
import { useAuthStore } from '../../store/authStore';
import { useCapabilities } from '../../hooks/useCapabilities';
import SpmiDictionaryModal from '../../components/laporan/SpmiDictionaryModal';

const STEPS = [
  { id: 1, title: 'Informasi Dasar', icon: <FileText className="w-4 h-4" /> },
  { id: 2, title: 'Referensi SPMI', icon: <Target className="w-4 h-4" /> },
  { id: 3, title: 'Detail Masalah', icon: <AlertTriangle className="w-4 h-4" /> },
  { id: 4, title: 'Dokumen Pendukung', icon: <Paperclip className="w-4 h-4" /> },
  { id: 5, title: 'Klasifikasi & Review', icon: <Eye className="w-4 h-4" /> },
];

interface FormData {
  kategori_masalah_id: number | null;
  standar_spmi_id: number | null;
  indikator_spmi_id: number | null;
  judul: string;
  deskripsi: string;
  dampak: string;
  akar_masalah: string;
  tingkat_risiko: TingkatRisiko | '';
  target_selesai: string;
}

interface ExistingFile { id: number; filename: string; original_name: string; size: number; mime_type: string; url: string | null; }
interface NewFile { id: string; file: File; name: string; size: number; type: string; preview?: string; }

const LaporanEdit: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings, fetchPublicSettings } = useSystemSettingsStore();
  const { user } = useAuthStore();
  const { canCreateLaporan } = useCapabilities();

  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [showSpmiDict, setShowSpmiDict] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [originalKode, setOriginalKode] = useState('');
  const [originalStatus, setOriginalStatus] = useState('');

  // Auto-save timer states
  const AUTO_SAVE_INTERVAL = 30;
  const [autoSaveCountdown, setAutoSaveCountdown] = useState(AUTO_SAVE_INTERVAL);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Master data from API
  const [kategoriList, setKategoriList] = useState<KategoriMasalah[]>([]);
  const [standarList, setStandarList] = useState<StandarSPMI[]>([]);
  const [indikatorList, setIndikatorList] = useState<IndikatorSPMI[]>([]);

  const [formData, setFormData] = useState<FormData>({
    kategori_masalah_id: null, standar_spmi_id: null, indikator_spmi_id: null,
    judul: '', deskripsi: '', dampak: '', akar_masalah: '', tingkat_risiko: '', target_selesai: ''
  });

  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [newFiles, setNewFiles] = useState<NewFile[]>([]);
  const [deletedFileIds, setDeletedFileIds] = useState<number[]>([]);

  // Load existing laporan + master data
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [laporanRes, katRes, stdRes, indRes] = await Promise.all([
          laporanService.getById(Number(id)),
          adminService.kategori.getAll().catch(() => []),
          adminService.standar.getAll().catch(() => []),
          adminService.indikator.getAll().catch(() => []),
        ]);
        const data = (laporanRes as any)?.data || laporanRes;
        setOriginalKode(data.kode || '');
        setOriginalStatus(data.status || '');
        setFormData({
          kategori_masalah_id: data.kategori_masalah_id,
          standar_spmi_id: data.standar_spmi_id,
          indikator_spmi_id: data.indikator_spmi_id,
          judul: data.judul || '',
          deskripsi: data.deskripsi || '',
          dampak: data.dampak || '',
          akar_masalah: data.akar_masalah || '',
          tingkat_risiko: data.tingkat_risiko || '',
          target_selesai: data.target_selesai || ''
        });
        setExistingFiles(data.attachments || []);
        const kat = Array.isArray(katRes) ? katRes : ((katRes as any)?.data || []);
        const std = Array.isArray(stdRes) ? stdRes : ((stdRes as any)?.data || []);
        const ind = Array.isArray(indRes) ? indRes : ((indRes as any)?.data || []);
        setKategoriList(kat);
        setStandarList(std);
        setIndikatorList(ind);
      } catch (e) { toast.error('Gagal memuat data laporan'); navigate('/laporan'); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [id, navigate]);

  // Fetch SLA settings
  useEffect(() => {
    fetchPublicSettings();
  }, [fetchPublicSettings]);

  // Guard: Capability check untuk edit laporan
  useEffect(() => {
    if (!loading && !canCreateLaporan) {
      toast.error('Anda tidak memiliki akses untuk mengedit laporan');
      navigate('/laporan');
    }
  }, [loading, canCreateLaporan, navigate]);

  const filteredIndikator = indikatorList.filter((ind) => ind.standar_spmi_id === formData.standar_spmi_id);

  // Update autoSave with status feedback
  const autoSave = useCallback(async () => {
    if (!formData.judul) return;
    setSaving(true);
    setAutoSaveStatus('saving');
    try {
      await laporanService.update(Number(id), formData as any);
      setLastSaved(new Date());
      setAutoSaveStatus('saved');
      setAutoSaveCountdown(AUTO_SAVE_INTERVAL);
      setTimeout(() => setAutoSaveStatus('idle'), 3000);
    }
    catch (e) {
      setAutoSaveStatus('error');
      setTimeout(() => setAutoSaveStatus('idle'), 5000);
    }
    finally { setSaving(false); }
  }, [formData, id, AUTO_SAVE_INTERVAL]);

  // Countdown timer for auto-save
  useEffect(() => {
    if (!formData.judul) {
      setAutoSaveEnabled(false);
      return;
    }

    setAutoSaveEnabled(true);

    const countdownInterval = setInterval(() => {
      setAutoSaveCountdown(prev => {
        if (prev <= 1) {
          autoSave();
          return AUTO_SAVE_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [formData.judul, autoSave, AUTO_SAVE_INTERVAL]);

  // Old interval removed in favor of countdown
  /* useEffect(() => { const interval = setInterval(autoSave, 30000); return () => clearInterval(interval); }, [autoSave]); */

  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) { setErrors((prev) => { const next = { ...prev }; delete next[field]; return next; }); }
  };

  const applySpmiReference = (standarId: number, indikatorId: number) => {
    setFormData((prev) => ({
      ...prev,
      standar_spmi_id: standarId,
      indikator_spmi_id: indikatorId,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.standar_spmi_id;
      delete next.indikator_spmi_id;
      return next;
    });
    setShowSpmiDict(false);
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    if (step === 1) {
      if (!formData.kategori_masalah_id) newErrors.kategori_masalah_id = 'Kategori wajib dipilih';
      if (!formData.judul.trim()) newErrors.judul = 'Judul wajib diisi';
    }
    if (step === 3) { if (!formData.deskripsi.trim()) newErrors.deskripsi = 'Deskripsi wajib diisi'; }
    if (step === 5) {
      if (!formData.tingkat_risiko) newErrors.tingkat_risiko = 'Tingkat risiko wajib dipilih';
      if (!formData.target_selesai) newErrors.target_selesai = 'Target selesai wajib diisi';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => { if (validateStep(currentStep)) setCurrentStep((prev) => Math.min(prev + 1, 5)); };
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const handleNewFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const maxSize = 10 * 1024 * 1024;
    const totalFiles = existingFiles.length - deletedFileIds.length + newFiles.length;
    if (totalFiles + files.length > 10) { toast.error('Maksimal 10 file per laporan'); return; }
    const validFiles: NewFile[] = [];
    for (const file of files) {
      if (file.size > maxSize) { toast.error(`File "${file.name}" melebihi 10MB`); continue; }
      validFiles.push({ id: Math.random().toString(36).substr(2, 9), file, name: file.name, size: file.size, type: file.type, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined });
    }
    setNewFiles((prev) => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeExistingFile = (fileId: number) => setDeletedFileIds((prev) => [...prev, fileId]);
  const restoreExistingFile = (fileId: number) => setDeletedFileIds((prev) => prev.filter((id) => id !== fileId));
  const removeNewFile = (fileId: string) => setNewFiles((prev) => prev.filter((f) => f.id !== fileId));

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await laporanService.update(Number(id), formData as any);
      for (const fileId of deletedFileIds) { await laporanService.deleteAttachment(Number(id), fileId); }
      for (const file of newFiles) { await laporanService.uploadAttachment(Number(id), file.file); }
      toast.success('Laporan berhasil disimpan');
      setLastSaved(new Date());
      setDeletedFileIds([]);
      setNewFiles([]);
    } catch (e) { toast.error('Gagal menyimpan laporan'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;
    setSubmitting(true);
    try {
      await laporanService.update(Number(id), formData as any);
      for (const fileId of deletedFileIds) { await laporanService.deleteAttachment(Number(id), fileId); }
      for (const file of newFiles) { await laporanService.uploadAttachment(Number(id), file.file); }
      await laporanService.submit(Number(id));
      toast.success('Laporan berhasil disubmit!');
      navigate('/laporan');
    } catch (e) { toast.error('Gagal mengirim laporan'); }
    finally { setSubmitting(false); }
  };

  const selectedKategori = kategoriList.find((k) => k.id === formData.kategori_masalah_id);
  const selectedStandar = standarList.find((s) => s.id === formData.standar_spmi_id);

  // Dynamic SLA dari settings (dengan fallback)
  const slaConfig = {
    rendah: settings.sla_rendah || 60,
    sedang: settings.sla_sedang || 30,
    tinggi: settings.sla_tinggi || 14,
    kritis: settings.sla_critical || 7
  };

  const risikoLabels: Record<string, { label: string; sla: string; slaDays: number; bg: string; border: string; activeBg: string; activeBorder: string; dot: string }> = {
    rendah: { label: 'Rendah', sla: `${slaConfig.rendah} hari`, slaDays: slaConfig.rendah, bg: '#ecfdf5', border: '#a7f3d0', activeBg: '#d1fae5', activeBorder: '#10b981', dot: '#10b981' },
    sedang: { label: 'Sedang', sla: `${slaConfig.sedang} hari`, slaDays: slaConfig.sedang, bg: '#fffbeb', border: '#fde68a', activeBg: '#fef3c7', activeBorder: '#f59e0b', dot: '#f59e0b' },
    tinggi: { label: 'Tinggi', sla: `${slaConfig.tinggi} hari`, slaDays: slaConfig.tinggi, bg: '#fff7ed', border: '#fed7aa', activeBg: '#ffedd5', activeBorder: '#f97316', dot: '#f97316' },
    kritis: { label: 'Kritis', sla: `${slaConfig.kritis} hari`, slaDays: slaConfig.kritis, bg: '#fef2f2', border: '#fecaca', activeBg: '#fee2e2', activeBorder: '#ef4444', dot: '#ef4444' }
  };

  const handleRiskChange = (risk: TingkatRisiko) => {
    updateField('tingkat_risiko', risk);
    const config = risikoLabels[risk];
    if (config) {
      const target = new Date();
      target.setDate(target.getDate() + config.slaDays);
      const year = target.getFullYear();
      const month = String(target.getMonth() + 1).padStart(2, '0');
      const day = String(target.getDate()).padStart(2, '0');
      updateField('target_selesai', `${year}-${month}-${day}`);
    }
  };

  if (loading) return <Loading variant="page" message="Memuat data laporan..." />;

  const AutoSaveIndicator = () => {
    if (!autoSaveEnabled) {
      return (
        <div className="flex items-center gap-2 text-xs text-neutral-text-secondary">
          <Clock className="w-3.5 h-3.5" />
          <span>Isi judul untuk mengaktifkan auto-save</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${autoSaveStatus === 'saving'
            ? 'bg-blue-50 text-blue-600 border border-blue-200'
            : autoSaveStatus === 'saved'
              ? 'bg-green-50 text-green-600 border border-green-200'
              : autoSaveStatus === 'error'
                ? 'bg-red-50 text-red-600 border border-red-200'
                : 'bg-neutral-bg text-neutral-text-secondary border border-neutral-border'
          }`}>
          {autoSaveStatus === 'saving' ? (
            <>
              <LogoLoadingMark size="xs" />
              <span>Menyimpan...</span>
            </>
          ) : autoSaveStatus === 'saved' ? (
            <>
              <CheckCircle className="w-3.5 h-3.5" />
              <span>Tersimpan</span>
            </>
          ) : autoSaveStatus === 'error' ? (
            <>
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Gagal</span>
            </>
          ) : (
            <>
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Auto-save: {autoSaveCountdown}s</span>
            </>
          )}
        </div>

        {lastSaved && (
          <div className="flex items-center gap-1.5 text-xs text-neutral-text-secondary">
            <Clock className="w-3 h-3" />
            <span>
              {lastSaved.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>
        )}
    </div>
  );
};

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Laporan Masalah', href: '/laporan' }, { label: 'Edit Laporan' }]} />

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Edit Laporan Masalah</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Ubah formulir berikut untuk memperbarui masalah evaluasi</p>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-sm font-mono text-teal-main bg-teal-main/5 px-2 py-0.5 rounded">{originalKode}</span>
            <Badge variant="status" status={originalStatus as any} />
            {(originalStatus === 'ditolak_l1' || originalStatus === 'ditolak_l2') && <span className="text-xs text-danger font-medium bg-danger/5 px-2 py-0.5 rounded">⚠ Perlu direvisi dan submit ulang</span>}
          </div>
        </div>
        <AutoSaveIndicator />
      </div>
      <Card padding="sm">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <button onClick={() => { if (step.id < currentStep) setCurrentStep(step.id); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${step.id === currentStep ? 'bg-teal-main text-white shadow-md' : step.id < currentStep ? 'bg-success/10 text-success cursor-pointer font-semibold' : 'text-neutral-text-secondary'}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step.id === currentStep ? 'bg-white/20' : step.id < currentStep ? 'bg-success/20 text-success' : 'bg-neutral-bg'}`}>
                  {step.id < currentStep ? <CheckCircle className="w-4 h-4" /> : step.id}
                </div>
                <span className="hidden md:inline">{step.title}</span>
              </button>
              {index < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${step.id < currentStep ? 'bg-success' : 'bg-neutral-border'}`} />}
            </React.Fragment>
          ))}
        </div>
      </Card>
      <Card>
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><FileText className="w-5 h-5" /><h2 className="text-lg font-bold">Informasi Dasar</h2></div>
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Kategori Masalah <span className="text-danger">*</span></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {kategoriList.map((kat) => (
                    <button key={kat.id} onClick={() => updateField('kategori_masalah_id', kat.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all h-full flex flex-col justify-between ${formData.kategori_masalah_id === kat.id ? 'border-teal-main bg-teal-main/5 shadow-md' : 'border-neutral-border hover:border-teal-light hover:bg-neutral-bg'}`}>
                      <div className="mb-3">
                        <div className="flex items-center gap-3"><div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: kat.color }} /><span className="font-semibold text-sm text-neutral-text-primary">{kat.nama}</span></div>
                        <p className="text-xs text-neutral-text-secondary mt-2 line-clamp-3">{kat.deskripsi}</p>
                      </div>
                      <div className="mt-auto pt-3 border-t border-neutral-border/60">
                        {kat.role_scopes && kat.role_scopes.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {kat.role_scopes.map((rs: any, idx: number) => rs.role?.display_name && (
                                <span key={idx} className="text-[10px] bg-teal-main/10 text-teal-dark px-1.5 py-0.5 rounded font-medium border border-teal-main/20">
                                  Ditangani: {rs.role.display_name}
                                </span>
                              ))}
                            </div>
                        ) : (
                            <span className="text-[10px] bg-neutral-100 text-neutral-text-secondary px-1.5 py-0.5 rounded font-medium border border-neutral-200">
                              Sesuai Disposisi
                            </span>
                        )}
                      </div>
                    </button>
                  ))}
              </div>
              {errors.kategori_masalah_id && <p className="text-xs text-danger">{errors.kategori_masalah_id}</p>}
            </div>
            <Input label="Judul Laporan" placeholder="Contoh: Kekurangan ruang laboratorium komputer" value={formData.judul} onChange={(e) => updateField('judul', e.target.value)} error={errors.judul} required maxLength={500} />
            <p className="text-xs text-neutral-text-secondary text-right">{formData.judul.length}/500</p>
          </div>
        )}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><Target className="w-5 h-5" /><h2 className="text-lg font-bold">Referensi SPMI</h2></div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="flex items-start gap-2 p-3 bg-info/5 border border-info/20 rounded-xl flex-1">
                <Info className="w-5 h-5 text-info shrink-0 mt-0.5" />
                <p className="text-sm text-info">Opsional namun disarankan untuk pelaporan akreditasi.</p>
              </div>
              <Button type="button" variant="outline" className="shrink-0" onClick={() => setShowSpmiDict(true)} icon={<BookOpen className="w-4 h-4" />}>
                Lihat Kamus SPMI
              </Button>
            </div>
            <Select label="Standar SPMI" options={standarList.map((s) => ({ value: s.id, label: `${s.kode} - ${s.nama}` }))} placeholder="Pilih standar SPMI (opsional)" value={formData.standar_spmi_id?.toString() || ''} onChange={(v) => { updateField('standar_spmi_id', v ? Number(v) : null); updateField('indikator_spmi_id', null); }} />
            {formData.standar_spmi_id && <Select label="Indikator SPMI" options={filteredIndikator.map((i) => ({ value: i.id, label: `${i.kode} - ${i.nama} (Target: ${i.target_value})` }))} placeholder="Pilih indikator (opsional)" value={formData.indikator_spmi_id?.toString() || ''} onChange={(v) => updateField('indikator_spmi_id', v ? Number(v) : null)} />}
          </div>
        )}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><AlertTriangle className="w-5 h-5" /><h2 className="text-lg font-bold">Detail Masalah</h2></div>
            <Textarea label="Deskripsi Masalah" placeholder="Jelaskan masalah secara detail..." value={formData.deskripsi} onChange={(e) => updateField('deskripsi', e.target.value)} error={errors.deskripsi} required maxLength={5000} showCount rows={6} />
            <Textarea label="Dampak Masalah" placeholder="Apa dampak dari masalah ini?" value={formData.dampak} onChange={(e) => updateField('dampak', e.target.value)} maxLength={3000} showCount rows={4} />
            <Textarea label="Akar Masalah" placeholder="Apa penyebab utama dari masalah ini?" value={formData.akar_masalah} onChange={(e) => updateField('akar_masalah', e.target.value)} maxLength={3000} showCount rows={4} />
          </div>
        )}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><Paperclip className="w-5 h-5" /><h2 className="text-lg font-bold">Dokumen Pendukung</h2></div>
            {existingFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">File Saat Ini</p>
                {existingFiles.map((file) => {
                  const isDeleted = deletedFileIds.includes(file.id);
                  return (
                    <div key={file.id} className={`flex items-center gap-3 p-3 rounded-lg transition-all ${isDeleted ? 'bg-danger/5 opacity-60 line-through' : 'bg-neutral-bg'}`}>
                      <div className="w-10 h-10 bg-teal-main/10 rounded flex items-center justify-center"><FileText className="w-5 h-5 text-teal-main" /></div>
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{file.original_name}</p><p className="text-xs text-neutral-text-secondary">{formatFileSize(file.size)}</p></div>
                      {isDeleted ? <Button variant="ghost" size="sm" onClick={() => restoreExistingFile(file.id)} className="text-success">Pulihkan</Button>
                        : <button onClick={() => removeExistingFile(file.id)} className="p-1.5 rounded-lg hover:bg-danger/10 text-neutral-text-secondary hover:text-danger transition-colors"><X className="w-4 h-4" /></button>}
                    </div>
                  );
                })}
              </div>
            )}
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-neutral-border rounded-xl hover:border-teal-light hover:bg-teal-main/5 transition-all cursor-pointer">
              <Upload className="w-8 h-8 text-neutral-text-secondary mb-2" /><p className="text-sm font-medium">Klik untuk upload file baru</p><p className="text-xs text-neutral-text-secondary mt-1">PDF, DOC, XLS, JPG, PNG (Maks 10MB)</p>
              <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleNewFileAdd} className="hidden" />
            </label>
            {newFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">File Baru ({newFiles.length})</p>
                {newFiles.map((file) => (
                  <div key={file.id} className="flex items-center gap-3 p-3 bg-success/5 rounded-lg border border-success/20">
                    {file.preview ? <img src={file.preview} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 bg-success/10 rounded flex items-center justify-center"><FileText className="w-5 h-5 text-success" /></div>}
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{file.name}</p><p className="text-xs text-neutral-text-secondary">{formatFileSize(file.size)}</p></div>
                    <span className="text-xs text-success font-semibold">BARU</span>
                    <button onClick={() => removeNewFile(file.id)} className="p-1.5 rounded-lg hover:bg-danger/10 text-neutral-text-secondary hover:text-danger transition-colors"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {currentStep === 5 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-teal-dark mb-2"><Eye className="w-5 h-5" /><h2 className="text-lg font-bold">Klasifikasi & Review</h2></div>
            <div className="space-y-2">
              <label className="block text-xs font-semibold uppercase tracking-widest text-neutral-text-secondary">Tingkat Risiko <span className="text-danger">*</span></label>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {(['rendah', 'sedang', 'tinggi', 'kritis'] as TingkatRisiko[]).map((risk) => {
                  const config = risikoLabels[risk];
                  const isSelected = formData.tingkat_risiko === risk;
                  return (
                    <button key={risk} onClick={() => handleRiskChange(risk)}
                      className="p-4 rounded-xl border-2 text-left transition-all"
                      style={{
                        backgroundColor: isSelected ? config.activeBg : config.bg,
                        borderColor: isSelected ? config.activeBorder : config.border,
                        boxShadow: isSelected ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none'
                      }}>
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: config.dot }} />
                        <Badge variant="risk" risk={risk} size="md" />
                      </div>
                      <p className="text-xs text-neutral-text-secondary mt-2">SLA: {config.sla}</p>
                    </button>
                  );
                })}
              </div>
              {errors.tingkat_risiko && <p className="text-xs text-danger">{errors.tingkat_risiko}</p>}
            </div>
            <Input label="Target Penyelesaian" type="date" value={formData.target_selesai} onChange={(e) => updateField('target_selesai', e.target.value)} error={errors.target_selesai} required />
            <div className="mt-8 p-6 bg-white rounded-xl border border-neutral-border shadow-sm">
              <h3 className="text-base font-bold text-teal-dark mb-5 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Ringkasan Perubahan
              </h3>
              <div className="divide-y divide-neutral-border/60">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 pb-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">Kategori</p>
                    <p className="text-sm font-semibold text-neutral-text-primary">{selectedKategori?.nama || '-'}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">Standar SPMI</p>
                    <p className="text-sm font-semibold text-neutral-text-primary">{selectedStandar ? `${selectedStandar.kode} - ${selectedStandar.nama}` : '-'}</p>
                  </div>
                </div>
                <div className="py-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">Judul</p>
                  <p className="text-sm font-semibold text-neutral-text-primary">{formData.judul || '-'}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 py-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-1">Risiko</p>
                    <div>{formData.tingkat_risiko ? <Badge variant="risk" risk={formData.tingkat_risiko as TingkatRisiko} size="md" /> : '-'}</div>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-1">Target</p>
                    <p className="text-sm font-semibold text-neutral-text-primary">{formData.target_selesai ? new Date(formData.target_selesai).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 pt-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">File Dihapus</p>
                    <p className="text-sm font-semibold text-danger">{deletedFileIds.length} file</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-text-secondary/70 mb-0.5">File Baru</p>
                    <p className="text-sm font-semibold text-success">{newFiles.length} file</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>
      <div className="flex items-center justify-between">
        <div>{currentStep > 1 && <Button variant="outline" onClick={prevStep} icon={<ChevronLeft className="w-4 h-4" />}>Sebelumnya</Button>}</div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={handleSave} loading={saving} icon={<Save className="w-4 h-4" />}>Simpan</Button>
          {currentStep < 5 ? <Button variant="primary" onClick={nextStep} icon={<ChevronRight className="w-4 h-4" />}>Selanjutnya</Button>
            : <Button variant="primary" onClick={handleSubmit} loading={submitting} icon={<Send className="w-4 h-4" />}>{['draf', 'draft'].includes(originalStatus) ? 'Submit Laporan' : 'Submit Ulang'}</Button>}
        </div>
      </div>

      {showSpmiDict && (
        <SpmiDictionaryModal
          isOpen={showSpmiDict}
          onClose={() => setShowSpmiDict(false)}
          standarList={standarList}
          indikatorList={indikatorList}
          selectedStandarId={formData.standar_spmi_id}
          selectedIndikatorId={formData.indikator_spmi_id}
          onSelectIndikator={({ standarId, indikatorId }) => applySpmiReference(standarId, indikatorId)}
        />
      )}
    </div>
  );
};

export default LaporanEdit;
